import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import Image, { ImageProps } from 'next/image';
import { Square, Maximize2 } from 'lucide-react';

interface ImageCropperProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedImage: Blob) => void;
  aspectRatio?: number | undefined;
}

// Create a combined type for the image ref
type CombinedImageRef = HTMLImageElement & ImageProps;

export function ImageCropper({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio
}: ImageCropperProps) {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 90,
    x: 5,
    y: 5
  });
  const [scale, setScale] = useState(1);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<CombinedImageRef>(null);
  const [lockAspectRatio, setLockAspectRatio] = useState<boolean | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  // Determine the actual aspect ratio to use
  const currentAspectRatio = lockAspectRatio === true ? 1 : (lockAspectRatio === false ? undefined : aspectRatio);

  // Function to center aspect crop
  const centerAspectCrop = useCallback((mediaWidth: number, mediaHeight: number, aspect: number) => {
    return centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }, []);

  // Reset crop and scale when dialog opens or image changes
  useEffect(() => {
    if (isOpen && imageSrc) {
      setCrop({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5
      });
      setScale(1);
      setCompletedCrop(null);
      setLockAspectRatio(null); // Reset to default (use prop or free-form)
    }
  }, [isOpen, imageSrc]);

  // Update crop when aspect ratio lock changes
  useEffect(() => {
    if (imgRef.current && lockAspectRatio !== null) {
      const { width, height } = imgRef.current;
      if (width && height) {
        const aspectToUse = lockAspectRatio === true ? 1 : undefined;
        if (aspectToUse) {
          setCrop(centerAspectCrop(width, height, aspectToUse));
        } else {
          // Free form - reset to default crop
          setCrop({
            unit: '%',
            width: 90,
            height: 90,
            x: 5,
            y: 5
          });
        }
      }
    }
  }, [lockAspectRatio, centerAspectCrop]);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY * -0.01;
    const newScale = Math.max(0.1, Math.min(10, scale + delta));
    setScale(newScale);
  }, [scale]);

  // Add event listener for mouse wheel
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const image = e.currentTarget;
    const { naturalWidth, naturalHeight, width, height } = image;

    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = 300; // Match the fixed height

      const scaleX = containerWidth / naturalWidth;
      const scaleY = containerHeight / naturalHeight;
      const fitScale = Math.min(scaleX, scaleY, 1);

      setImageSize({
        width: naturalWidth * fitScale,
        height: naturalHeight * fitScale
      });
    }

    // Initialize crop with aspect ratio if provided
    const aspectToUse = lockAspectRatio === true ? 1 : (lockAspectRatio === false ? undefined : aspectRatio);
    if (aspectToUse && width && height) {
      setCrop(centerAspectCrop(width, height, aspectToUse));
    }

    image.addEventListener('wheel', handleWheel, { passive: false });
    return () => image.removeEventListener('wheel', handleWheel);
  }, [handleWheel, aspectRatio, centerAspectCrop, lockAspectRatio]);

  const getCroppedImg = async (
    image: HTMLImageElement,
    crop: PixelCrop
  ): Promise<Blob> => {
    const canvas = document.createElement('canvas');

    // Calculate display vs natural image ratio
    // Use the calculated imageSize for display dimensions if available, otherwise rect
    const rect = image.getBoundingClientRect();
    const displayToNaturalRatio = {
      width: image.naturalWidth / rect.width,
      height: image.naturalHeight / rect.height
    };

    // Calculate dimensions taking scale into account
    // Since the image width/height are already scaled via CSS, we just need the ratio
    // between the displayed (scaled) size and the natural size.
    const scaledCrop = {
      x: crop.x * displayToNaturalRatio.width,
      y: crop.y * displayToNaturalRatio.height,
      width: crop.width * displayToNaturalRatio.width,
      height: crop.height * displayToNaturalRatio.height
    };

    // Set canvas size to match the cropped area
    canvas.width = scaledCrop.width;
    canvas.height = scaledCrop.height;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    // Enable high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw the cropped image 1:1 from natural source to canvas
    ctx.drawImage(
      image,
      scaledCrop.x,
      scaledCrop.y,
      scaledCrop.width,
      scaledCrop.height,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Convert to blob with high quality
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/png',
        1.0  // Maximum quality
      );
    });
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) return;

    try {
      const blob = await getCroppedImg(imgRef.current, completedCrop);
      onCropComplete(blob);
      onClose();
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Crop Image</DialogTitle>
        </DialogHeader>

        <div
          ref={containerRef}
          className="mt-4 flex overflow-auto h-[300px] w-full relative bg-black/5 rounded-md"
        >
          <div className="m-auto">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={currentAspectRatio}
            >
              <Image
                ref={imgRef as React.RefObject<CombinedImageRef>}
                src={imageSrc}
                alt="Crop me"
                width={800}
                height={600}
                style={{
                  width: imageSize.width ? `${imageSize.width * scale}px` : 'auto',
                  height: imageSize.height ? `${imageSize.height * scale}px` : 'auto',
                  maxWidth: 'none',
                }}
                className="object-contain"
                onLoad={handleImageLoad}
                unoptimized
              />
            </ReactCrop>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={lockAspectRatio === true ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const newLock = lockAspectRatio === true ? null : true;
                  setLockAspectRatio(newLock);
                  if (imgRef.current && newLock === true) {
                    const { width, height } = imgRef.current;
                    if (width && height) {
                      setCrop(centerAspectCrop(width, height, 1));
                    }
                  }
                }}
                className="flex items-center gap-2"
              >
                <Square className="h-4 w-4" />
                Square (1:1)
              </Button>
              <Button
                type="button"
                variant={lockAspectRatio === false ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const newLock = lockAspectRatio === false ? null : false;
                  setLockAspectRatio(newLock);
                  if (imgRef.current && newLock === false) {
                    // Free form - reset to default crop
                    setCrop({
                      unit: '%',
                      width: 90,
                      height: 90,
                      x: 5,
                      y: 5
                    });
                  }
                }}
                className="flex items-center gap-2"
              >
                <Maximize2 className="h-4 w-4" />
                Free Form
              </Button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Zoom: {scale.toFixed(2)}x
            </label>
            <Slider
              value={[scale]}
              onValueChange={(value) => setScale(value[0])}
              min={0.1}
              max={3}
              step={0.1}
              className="w-full"
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => {
              setScale(1);
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCropComplete}>
            Apply Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 